import { ChecklistItemsInput, ConfigureChecklistInput, TaskChecklist } from './task-checklist.js';
import { z } from 'zod';
import { TaskStatusRules } from './task-status-policy.js';
import { TaskScheduleRules } from './task-schedule-policy.js';
import { MAX_MIN_TASK_DURATION_MINUTES } from './task-min-duration.js';
import { PlanField } from './task-plan-gate.js';
/**
 * Project & Task — APM-501.
 *
 * **Một** data model dùng chung cho cả Agile lẫn Waterfall (spec mục 2); khác
 * nhau chỉ ở cách hiển thị. Board Kanban và Gantt đọc cùng bảng `task`.
 */
/**
 * `MAINTAIN` là loại thứ ba, không phải một biến thể của Agile — APM-MAINT-a.
 *
 * Thứ tách nó ra: dự án bảo trì **không có ngày kết thúc**, nó chạy tới khi PM
 * dừng. Mọi báo cáo đo tiến độ so với một mốc kết thúc đều không áp dụng được,
 * nên đây không phải một nhãn hiển thị — nó đổi cả bộ màn hình của dự án.
 */
export const ProjectMethodology = z.enum(['AGILE', 'WATERFALL', 'MAINTAIN']);
/**
 * Việc nằm ở **backlog** của dự án Agile — chưa vào sprint nào.
 *
 * Một hàm cho cả cửa ở lệnh (`assertProgressWritable`) lẫn cờ `TaskView.inBacklog`,
 * nên giao diện và server không thể nói hai điều khác nhau. Waterfall và Bảo trì
 * không có sprint: `sprintId` của chúng luôn `null` mà không có nghĩa "backlog".
 */
export function isAgileBacklog(methodology, sprintId) {
    return methodology === 'AGILE' && sprintId === null;
}
/**
 * `pending_approval` — vừa tạo, chờ quản lý trực tiếp duyệt (APM-ROLE-m).
 *
 * ⚠️ Dự án ở trạng thái này **chưa tồn tại** theo nghĩa nghiệp vụ: không vào
 * dashboard portfolio, không vào báo cáo tổ chức, không vào velocity. Cùng luật
 * với `isDemo` và cùng lý do — một dự án chưa ai đồng ý cho mở mà đã kéo tụt chỉ
 * số của cả tổ chức thì con số ấy nói dối.
 */
export const ProjectStatus = z.enum([
    'pending_approval',
    'planning',
    'active',
    'on_hold',
    'completed',
    'archived',
]);
export const WorkflowCategory = z.enum(['todo', 'in_progress', 'done']);
export const TaskPriority = z.enum(['low', 'medium', 'high', 'urgent']);
/** Mức ưu tiên của **dự án** — APM-PSET-prio. Cùng bốn mức với task, nhưng là enum riêng. */
export const ProjectPriority = z.enum(['low', 'medium', 'high', 'urgent']);
export const DependencyType = z.enum([
    'finish_to_start',
    'start_to_start',
    'finish_to_finish',
    'start_to_finish',
]);
/** `YYYY-MM-DD`. Ngày lịch, không phải thời điểm — không kèm giờ, không kèm múi. */
export const CalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày phải có dạng YYYY-MM-DD');
// ── Loại task ───────────────────────────────────────────────────────────────
/**
 * Ba mươi màu nền cho nhãn loại việc — người dùng yêu cầu 2026-09-25.
 *
 * Danh sách **đóng**: một ô nhập mã màu tự do cho ra những nhãn không đọc được
 * (chữ vàng trên nền vàng) mà không ai phát hiện trước khi nó lên board. Chữ
 * trên nhãn tự chọn trắng hay đen theo độ sáng của nền (`TaskTypeTag`).
 *
 * ⚠️ Chỉ **thêm vào cuối**: màu đã lưu ở `task_type.color` là chính chuỗi hex,
 * nên xoá hay đổi một phần tử làm nhãn cũ mất màu trong bảng chọn.
 */
export const TASK_TYPE_COLORS = [
    '#dc2626',
    '#ea580c',
    '#d97706',
    '#ca8a04',
    '#65a30d',
    '#16a34a',
    '#059669',
    '#0d9488',
    '#0891b2',
    '#0284c7',
    '#2563eb',
    '#4f46e5',
    '#7c3aed',
    '#9333ea',
    '#c026d3',
    '#db2777',
    '#e11d48',
    '#78716c',
    '#475569',
    '#1e293b',
    '#fca5a5',
    '#fdba74',
    '#fcd34d',
    '#bef264',
    '#86efac',
    '#5eead4',
    '#7dd3fc',
    '#a5b4fc',
    '#d8b4fe',
    '#f9a8d4',
];
export const TaskTypeColor = z.enum(TASK_TYPE_COLORS);
export const TaskTypeStatus = z.enum(['approved', 'pending', 'rejected']);
export const TaskTypeView = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    color: z.string().nullable(),
    isActive: z.boolean(),
    /**
     * Trạng thái duyệt — APM-TT-e. PM đề xuất loại mới vào danh mục **chung** của
     * tổ chức; quản trị tổ chức duyệt xong (`approved`) mới dùng được. Ô chọn và
     * danh mục cho người thường chỉ có `approved`; màn quản trị thấy cả ba.
     */
    status: TaskTypeStatus,
    /** Bao nhiêu task đang dùng loại này — để admin biết tắt nó ảnh hưởng gì. */
    taskCount: z.number().int().nonnegative(),
});
export const TaskTypeInput = z.object({
    name: z.string().trim().min(1).max(60),
    /** Một trong `TASK_TYPE_COLORS`. Vắng mặt = nhãn tự có màu theo id. */
    color: TaskTypeColor.optional(),
});
/**
 * Đề xuất một loại việc mới — APM-TT-e. Từ Cấu hình dự án (kèm dự án, duyệt xong
 * tự gắn vào đó) hoặc từ form tạo dự án (chưa có dự án). Bắt buộc có màu.
 */
export const CreateProjectTaskTypeInput = z.object({
    name: z.string().trim().min(1).max(60),
    color: TaskTypeColor,
});
/**
 * - `proposed`: đã gửi, chờ quản trị tổ chức duyệt (quản trị nhận thông báo);
 * - `in_org_catalog`: tên ấy đã có và đã duyệt — bật loại chung, không tạo bản
 *   sao (hai loại cho cùng một việc là velocity tách đôi);
 * - `already_pending`: đã có người đề xuất tên này, đang chờ duyệt;
 * - `added`: chủ tổ chức cá nhân thêm — không ai để duyệt, nên loại vào danh mục
 *   ở trạng thái đã duyệt và (có dự án) đã gắn vào dự án, dùng được ngay
 *   (APM-PSPACE-o).
 */
export const CreateProjectTaskTypeResult = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('proposed'), taskTypeId: z.uuid() }),
    z.object({ kind: z.literal('added'), taskTypeId: z.uuid(), name: z.string() }),
    z.object({ kind: z.literal('in_org_catalog'), taskTypeId: z.uuid(), name: z.string() }),
    z.object({ kind: z.literal('already_pending'), taskTypeId: z.uuid(), name: z.string() }),
]);
/** Một đề xuất, nhìn từ màn quản trị hoặc từ dự án đã đề xuất. */
export const TaskTypeProposalView = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
    status: TaskTypeStatus,
    proposedByName: z.string().nullable(),
    proposedAt: z.iso.datetime(),
    projectId: z.uuid().nullable(),
    projectName: z.string().nullable(),
    reviewNote: z.string().nullable(),
});
/** Quản trị duyệt / từ chối. Từ chối bắt buộc có lý do — người đề xuất cần biết vì sao. */
export const ReviewTaskTypeInput = z
    .object({
    approve: z.boolean(),
    note: z.string().trim().max(500).nullable().default(null),
})
    .refine((input) => input.approve || (input.note !== null && input.note.length > 0), {
    path: ['note'],
    message: 'Cần lý do khi từ chối',
});
/**
 * Bảng màu nhận dạng của dự án — APM-PSET-e.
 *
 * Người dùng chọn một màu cho mỗi dự án, và nó tô nhãn tab "Điều lệ" cùng hàng
 * trên thanh bên. Mục đích duy nhất là **phân biệt**: không màu nào tốt hay xấu
 * hơn màu nào, và không màu nào mang nghĩa trạng thái (trạng thái đã có đèn RAG
 * ngay cạnh, với bảng màu riêng).
 *
 * ## Ba mươi màu, cùng bảng với loại việc — người dùng đổi 2026-09-25
 *
 * Bản trước là 20 màu cùng độ sáng và độ đậm, chỉ khác sắc độ 18°, dùng ở dạng
 * nhạt — người dùng báo "rất khó phân biệt". Nay là đúng 30 màu của
 * `TASK_TYPE_COLORS` (hàng đậm + hàng nhạt) và nhãn dùng **nền đặc**. 20 tên cũ
 * giữ nguyên (dữ liệu đã lưu không phải đổi), chỉ trỏ sang màu mới; thêm 10 tên.
 * ⚠️ Chỉ **thêm** tên, không xoá — tên đã lưu mà mất khỏi bảng thì dự án mất màu.
 *
 * ⚠️ Lưu **tên màu**, không lưu mã hex. Ba lý do:
 *
 * 1. Chế độ tối cần một giá trị khác cho cùng một màu. Hex lưu trong cơ sở dữ
 *    liệu chỉ đúng ở một chế độ, và sai ở chế độ kia — im lặng.
 * 2. Tailwind quét **mã nguồn** để sinh lớp, nên một mã màu tuỳ ý không có lớp
 *    nào; phải viết `style` tay ở từng chỗ dùng.
 * 3. Bảng đóng thì đổi cả bộ màu là sửa một chỗ, không phải đi tìm mọi hàng đã
 *    lưu.
 *
 * Tên ở đây là **khoá**, không phải chữ hiện ra: giao diện tra `projectColor.*`
 * trong i18n.
 */
export const PROJECT_COLORS = [
    'red',
    'orange',
    'amber',
    'gold',
    'lime',
    'green',
    'emerald',
    'teal',
    'cyan',
    'sky',
    'blue',
    'indigo',
    'violet',
    'purple',
    'magenta',
    'pink',
    'rose',
    'stone',
    'slate',
    'graphite',
    'blush',
    'peach',
    'yellow',
    'olive',
    'mint',
    'aqua',
    'ice',
    'azure',
    'lavender',
    'candy',
];
export const ProjectColor = z.enum(PROJECT_COLORS);
// ── Project ─────────────────────────────────────────────────────────────────
/**
 * Mã dự án dài tối đa bao nhiêu ký tự — người dùng chốt 2026-09-26 (trước là 10).
 *
 * Đo theo chỗ chật nhất mã phải hiện: sidebar **thu gọn** (48px) chứa đúng 6
 * ký tự mono cỡ 10px trên một dòng. Mã dài hơn phải xuống dòng ở đó. Mã đặt
 * trước khi hạ giới hạn vẫn giữ nguyên (mã không đổi được sau khi tạo) và
 * sidebar vẫn xuống dòng cho chúng.
 */
export const PROJECT_KEY_MAX = 6;
/** Một cột board dựng sẵn lúc tạo dự án — APM-SYNC-c. */
export const ProjectWorkflowStatusSeed = z.object({
    name: z.string().trim().min(1).max(60),
    category: WorkflowCategory,
});
export const CreateProjectInput = z.object({
    /** Mã ngắn hiển thị trên task (`APM-123`). **Không đổi được** sau khi tạo. */
    key: z
        .string()
        .min(2)
        .max(PROJECT_KEY_MAX, `Mã dự án tối đa ${PROJECT_KEY_MAX} ký tự`)
        .regex(/^[A-Z][A-Z0-9]*$/, 'Mã dự án chỉ gồm chữ HOA và số, bắt đầu bằng chữ'),
    name: z.string().trim().min(1).max(200),
    description: z.string().max(5_000).optional(),
    methodology: ProjectMethodology,
    /** Bỏ trống = `medium`. */
    priority: ProjectPriority.optional(),
    startDate: CalendarDate.optional(),
    endDate: CalendarDate.optional(),
    /**
     * Loại task project này dùng. Chọn lúc tạo, sửa được sau ở cài đặt project.
     * Bỏ trống = dùng mọi loại đang bật của tổ chức.
     */
    taskTypeIds: z.array(z.uuid()).optional(),
    /** Tech stack — spec mục 9 coi đây là input **bắt buộc** cho AI sinh WBS. */
    techStackIds: z.array(z.uuid()).optional(),
    /** Trần độ dài task tầng 1, ngày (spec 9.1). Agile đặt nhỏ, Waterfall đặt lớn. */
    wbsMaxDays: z.number().int().min(1).max(60).optional(),
    /**
     * Quản lý trực tiếp để duyệt dự án này — APM-ROLE-m.
     *
     * Chỉ cần khi người tạo **chưa có** quản lý trực tiếp và bản thân không mang
     * vai trò quản lý. Ghi thẳng vào `user.line_manager_id`, không phải một trường
     * riêng chỉ dùng cho lần duyệt này: người ta chỉ có một quản lý trực tiếp, và
     * giữ hai chỗ cho cùng một sự thật là mở đường cho chúng lệch nhau.
     */
    lineManagerId: z.uuid().optional(),
    /**
     * Cột board dựng sẵn thay cho ba cột mặc định — APM-SYNC-c, docs/plan-project-sync.md §6.3.
     *
     * Dự án tạo từ một nguồn đồng bộ dựng workflow y danh sách trạng thái bên ngoài
     * (tên, thứ tự, nhóm) để ánh xạ 1-1 (Q2). Nhóm nào thiếu thì handler thêm cột mặc
     * định của nhóm đó vào **cuối** — burndown, cycle time cần đủ ba nhóm.
     * Bỏ trống = ba cột mặc định như cũ.
     */
    workflowStatuses: z.array(ProjectWorkflowStatusSeed).min(1).max(30).optional(),
});
/** Quyết định của người duyệt một dự án đang chờ — APM-ROLE-m. */
export const ProjectDecisionInput = z.object({
    approve: z.boolean(),
    /**
     * Lý do. Từ chối mà không nói vì sao thì người tạo chỉ biết là "không được",
     * và họ sẽ tạo lại một dự án gần giống để thử lần nữa.
     */
    note: z.string().max(500).nullable().optional(),
});
/**
 * Một hàng trong hàng đợi duyệt — APM-ROLE-m3.
 *
 * Director và quản trị tổ chức thấy **mọi** dự án chờ duyệt của tổ chức, không
 * chỉ dự án được chỉ định cho họ. Nên hàng phải nói rõ ai đang chờ ai: duyệt
 * thay một người mà không biết đó là ai thì không khác gì duyệt mù.
 */
export const PendingProjectView = z.object({
    project: z.lazy(() => ProjectView),
    /** `null` khi người tạo đã rời tổ chức. */
    requestedByName: z.string().nullable(),
    /** Quản lý trực tiếp được chỉ định lúc tạo. */
    approverName: z.string().nullable(),
    /** `false` ⇒ người xem đang **duyệt thay** người khác. */
    assignedToMe: z.boolean(),
});
/**
 * Biên của "nhắc khi còn X% thời gian tới hạn" — APM-TLOW-b. Dưới 5% thì việc một ngày được
 * nhắc khi chỉ còn vài phút; trên 50% thì "sắp hết thời gian" bật ra khi mới làm được nửa.
 */
export const TIME_LOW_PCT_MIN = 5;
export const TIME_LOW_PCT_MAX = 50;
export const TIME_LOW_PCT_DEFAULT = 10;
export const UpdateProjectInput = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(5_000).nullable().optional(),
    status: ProjectStatus.optional(),
    priority: ProjectPriority.optional(),
    startDate: CalendarDate.nullable().optional(),
    endDate: CalendarDate.nullable().optional(),
    /**
     * Màu nhận dạng — APM-PSET-e.
     *
     * Ba giá trị ba nghĩa, như `dueSoonDays` ngay dưới: vắng mặt = không đụng
     * tới, `null` = bỏ màu, một tên = đặt màu ấy.
     */
    color: ProjectColor.nullable().optional(),
    taskTypeIds: z.array(z.uuid()).optional(),
    techStackIds: z.array(z.uuid()).optional(),
    wbsMaxDays: z.number().int().min(1).max(60).optional(),
    /**
     * Báo trước hạn, riêng cho dự án này — APM-NOTI-h.
     *
     * Ba giá trị, ba nghĩa khác nhau, và gộp bất kỳ hai cái nào là hỏng:
     * vắng mặt = **không đụng tới**; `null` = **quay về kế thừa** mặc định của tổ
     * chức; một số = dự án tự quyết.
     */
    dueSoonDays: z.number().int().min(1).max(30).nullable().optional(),
    /**
     * Thời gian tối thiểu (phút) từ lúc bắt đầu tới lúc được chuyển sang Xong —
     * APM-TMIN-a. Vắng = không đụng tới; `null` = tắt.
     */
    minTaskDurationMinutes: z
        .number()
        .int()
        .min(1)
        .max(MAX_MIN_TASK_DURATION_MINUTES)
        .nullable()
        .optional(),
    /**
     * Bắt buộc có hạn và ước lượng giờ trước khi việc sang "đang làm" / "xong" —
     * APM-EREF-a. Vắng = không đụng tới.
     */
    requirePlanBeforeStart: z.boolean().optional(),
    /**
     * Hai kiểu nhắc hạn bật / tắt độc lập — APM-TLOW-b (người dùng chốt 2026-10-15). Vắng = không
     * đụng tới. `timeLowPct` sửa được cả khi đang tắt: bật lại là dùng đúng số đã đặt.
     */
    dueSoonEnabled: z.boolean().optional(),
    timeLowEnabled: z.boolean().optional(),
    timeLowPct: z.number().int().min(TIME_LOW_PCT_MIN).max(TIME_LOW_PCT_MAX).optional(),
    /** Ai được đổi trạng thái / chuyển sang Done — APM-TSP-a. Gửi cả hai rule một lượt. */
    taskStatusRules: TaskStatusRules.optional(),
    /** Ai được đổi ngày bắt đầu / hạn của task — APM-TSCH-a. */
    taskScheduleRules: TaskScheduleRules.optional(),
});
/**
 * Đổi loại dự án — APM-PMETH.
 *
 * Lệnh riêng, **không** là một trường của `UpdateProjectInput`: đổi loại kéo
 * theo những thay đổi ở aggregate khác (việc định kỳ bị tạm dừng) và một ngày
 * kết thúc bị xoá, và người dùng phải thấy hết chúng **trước** khi bấm. Trộn vào
 * nút Lưu của form sửa thì một lần đổi tên vô tình mang theo một lần đổi loại.
 */
export const ChangeProjectMethodologyInput = z.object({ methodology: ProjectMethodology });
/**
 * Lý do **không** đổi được — có một cái là nút bị khoá.
 *
 * - `SAME_METHODOLOGY`: đích trùng loại hiện tại.
 * - `SPRINT_ACTIVE`: Agile đang chạy sprint. Đóng sprint là một quyết định
 *   (việc dở đi đâu), nên không làm ngầm thay PM — người dùng chốt 2026-10-05.
 * - `PROJECT_CLOSED`: dự án đã xong / lưu trữ / dự án bảo trì đã dừng. Mở lại trước.
 */
export const MethodologyChangeBlocker = z.discriminatedUnion('code', [
    z.object({ code: z.literal('SAME_METHODOLOGY') }),
    z.object({ code: z.literal('SPRINT_ACTIVE'), sprintName: z.string() }),
    z.object({ code: z.literal('PROJECT_CLOSED'), status: ProjectStatus }),
]);
/**
 * Thứ người dùng **mất đi** (hoặc thôi thấy) sau khi đổi — hiện thành cảnh báo.
 *
 * Chỉ liệt kê thứ dự án này **thật sự có**: một dự án chưa có sprint nào không
 * cần nghe "sprint sẽ bị ẩn". Cảnh báo luôn hiện dù không có gì để mất sẽ bị học
 * cách bấm qua — đúng lúc nó cần được đọc.
 *
 * - `SPRINTS_HIDDEN`: tab Sprint, velocity, review/retro ẩn đi. Dữ liệu **giữ
 *   nguyên** (cả `sprint_id` của task), đổi lại Agile là hiện lại.
 * - `END_DATE_CLEARED`: dự án bảo trì không có ngày kết thúc — xoá hẳn.
 * - `SCHEDULE_REPORTS_HIDDEN`: nhóm báo cáo tiến độ (burndown, EVM, baseline,
 *   đường găng) đo so với một mốc kết thúc, nên tắt ở dự án bảo trì.
 * - `RECURRENCES_PAUSED`: rời Bảo trì thì các lịch định kỳ đang bật chuyển sang
 *   tạm dừng; task đã sinh giữ nguyên.
 * - `FLOW_REPORTS_HIDDEN`: nhóm báo cáo bảo trì (thông lượng, tồn đọng) tắt.
 */
export const MethodologyChangeLoss = z.discriminatedUnion('code', [
    z.object({
        code: z.literal('SPRINTS_HIDDEN'),
        sprints: z.number().int().nonnegative(),
        tasks: z.number().int().nonnegative(),
    }),
    z.object({ code: z.literal('END_DATE_CLEARED'), endDate: CalendarDate }),
    z.object({ code: z.literal('SCHEDULE_REPORTS_HIDDEN') }),
    z.object({ code: z.literal('RECURRENCES_PAUSED'), count: z.number().int().positive() }),
    z.object({ code: z.literal('FLOW_REPORTS_HIDDEN') }),
]);
export const MethodologyChangeImpact = z.object({
    from: ProjectMethodology,
    to: ProjectMethodology,
    blockers: z.array(MethodologyChangeBlocker),
    losses: z.array(MethodologyChangeLoss),
});
export const WorkflowStatusView = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    category: WorkflowCategory,
    position: z.number().int().nonnegative(),
    color: z.string().nullable(),
    wipLimit: z.number().int().positive().nullable(),
    /** Cột nhận việc mới của **sprint chứa nó** — một cột mỗi (dự án, sprint). */
    isDefault: z.boolean(),
    /**
     * Cột này thuộc sprint nào — APM-BOARD-a1.
     *
     * `null` = cột ngoài sprint (Backlog, "Chờ duyệt"…), **luôn hiện** ở mọi
     * sprint. Đây cũng là giá trị của mọi cột có từ trước bản này.
     */
    sprintId: z.uuid().nullable(),
});
/**
 * Bố cục thanh tab của một dự án — APM-TAB-b.
 *
 * ## Phần đè, KHÔNG phải danh sách tab được hiện
 *
 * Đây là quyết định quan trọng nhất của tính năng, và nó đến từ câu hỏi "sau
 * này thêm trang mới thì sao".
 *
 * Nếu đây là *danh sách tab được hiện*, thì đúng ngày trang "Nghiệm thu" lên
 * sóng, mọi dự án đã tồn tại đều không có id ấy trong danh sách của mình — nên
 * tab mới **ẩn ở khắp nơi, im lặng**. Không lỗi, không cảnh báo; tính năng chỉ
 * đơn giản là không xuất hiện, và người đi tìm nguyên nhân sẽ tìm ở phía tính
 * năng mới chứ không phải ở đây.
 *
 * Là phần đè thì id lạ mặc định **hiện**, và chỉ thứ PM cố ý tắt mới tắt.
 *
 * ## Vì sao server không kiểm id theo danh mục tab
 *
 * Danh mục tab nằm ở frontend (`features/projects/tab-registry.ts`), nơi duy
 * nhất biết tab nào tồn tại và tab nào là trụ. Bắt server giữ một bản sao là
 * buộc hai bên phải deploy khớp nhau mới thêm được một trang — và bản sao ấy sẽ
 * trôi khỏi bản gốc.
 *
 * Nên server chỉ chặn thứ nó thật sự biết: **số lượng** và **độ dài chuỗi**.
 * Id lạ là chuyện bình thường: một tab đã gỡ khỏi mã nguồn vẫn còn tên trong dữ
 * liệu của mọi dự án đã cấu hình, và phía đọc bỏ qua nó.
 */
const TabId = z.string().min(1).max(40);
/** Trần số phần tử. Rộng hơn số tab thật rất nhiều — nó chỉ để chặn rác. */
const TAB_LAYOUT_LIMIT = 50;
export const ProjectTabLayout = z.object({
    /**
     * Thứ tự PM đã sắp. Id vắng mặt xếp sau, giữ thứ tự tự nhiên của mã nguồn —
     * nên tab mới rơi xuống cuối hàng chứ không biến mất.
     */
    order: z.array(TabId).max(TAB_LAYOUT_LIMIT),
    /** Id PM đã tắt. Tab trụ bỏ qua danh sách này — xem `tab-registry.ts`. */
    hidden: z.array(TabId).max(TAB_LAYOUT_LIMIT),
    /**
     * Id của tab **mặc định ẩn** mà PM đã bật (vd. Git) — APM-GIT-p.
     *
     * Phần đè theo chiều ngược lại với `hidden`: tab mặc định ẩn chỉ hiện khi có
     * tên ở đây. Tuỳ chọn để mọi bố cục đã lưu trước đó vẫn hợp lệ — vắng mặt
     * nghĩa là "chưa bật tab mặc định ẩn nào".
     */
    shown: z.array(TabId).max(TAB_LAYOUT_LIMIT).optional(),
});
/** Thân của `PATCH /projects/:id/tab-layout`. Gửi trọn bộ, không vá từng phần. */
export const UpdateTabLayoutInput = ProjectTabLayout;
export const ProjectView = z.object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    methodology: ProjectMethodology,
    status: ProjectStatus,
    priority: ProjectPriority,
    startDate: CalendarDate.nullable(),
    endDate: CalendarDate.nullable(),
    /** Màu nhận dạng do người dùng chọn — `null` = chưa chọn, giao diện dùng xám. */
    color: ProjectColor.nullable(),
    wbsMaxDays: z.number().int().positive(),
    /** `null` = kế thừa `notification.due_soon_days` của tổ chức — APM-NOTI-h. */
    dueSoonDays: z.number().int().positive().nullable(),
    /** Thời gian tối thiểu (phút) trước khi được chuyển task sang Xong — APM-TMIN-a. `null` = tắt. */
    minTaskDurationMinutes: z.number().int().positive().nullable(),
    /** Bắt buộc có hạn và ước lượng giờ trước khi làm — APM-EREF-a. */
    requirePlanBeforeStart: z.boolean(),
    /** Nhắc trước hạn `dueSoonDays` ngày có bật không — APM-TLOW-b. */
    dueSoonEnabled: z.boolean(),
    /** Nhắc khi việc đang làm còn ≤ `timeLowPct`% thời gian tới hạn có bật không — APM-TLOW-b. */
    timeLowEnabled: z.boolean(),
    timeLowPct: z.number().int(),
    /** Ai được đổi trạng thái / chuyển sang Done — APM-TSP-a. */
    taskStatusRules: TaskStatusRules,
    /** Ai được đổi ngày bắt đầu / hạn của task — APM-TSCH-a. */
    taskScheduleRules: TaskScheduleRules,
    taskTypes: z.array(TaskTypeView),
    techStackIds: z.array(z.uuid()),
    statuses: z.array(WorkflowStatusView),
    taskCount: z.number().int().nonnegative(),
    /**
     * Dự án mẫu — APM-1101, spec 18.2 đòi "đánh dấu tách biệt rõ ràng (nhãn DEMO
     * trên toàn bộ giao diện của dự án này)".
     *
     * Nằm trong view chứ không suy từ tên: người dùng đổi tên dự án demo là
     * chuyện bình thường (họ được sửa thoải mái), và khi ấy một nhãn dựa trên
     * chuỗi tên sẽ biến mất đúng lúc nó vẫn cần thiết.
     */
    isDemo: z.boolean(),
    /**
     * Bố cục thanh tab PM đã sắp — APM-TAB-b. `null` = chưa ai đụng tới.
     *
     * `null` khác hẳn `{ order: [], hidden: [] }` về mặt ý nghĩa ("chưa cấu hình"
     * so với "đã cấu hình, không đè gì"), dù hai thứ vẽ ra cùng một thanh tab.
     * Giữ khác biệt ấy để màn hình cấu hình nói đúng trạng thái.
     */
    tabLayout: ProjectTabLayout.nullable(),
    createdAt: z.iso.datetime(),
});
// ── Task ────────────────────────────────────────────────────────────────────
/**
 * Bản chất của một hạng mục trên lịch — **không phải** phân loại nghiệp vụ.
 *
 * Hai trục khác nhau, và chuẩn quản lý dự án quốc tế (PMBOK, MS Project,
 * Primavera) tách chúng vì cùng lý do:
 *
 * - `kind` là **hình học của lịch**: `task` có thời lượng, `milestone` là một
 *   mốc không có thời lượng, `phase_gate` là cổng giữa hai pha. Nó quyết định
 *   Gantt vẽ thanh hay hình thoi, và đường găng tính thế nào.
 * - `task_type` (Tính năng, Sửa lỗi, Kiểm thử…) là **nhãn nghiệp vụ**, tuỳ tổ
 *   chức đặt, sửa được ở trang quản trị.
 *
 * ⚠️ Nhét "Mốc" vào danh mục `task_type` sẽ hỏng cả hai: một mốc vẫn cần phân
 * loại được theo nghiệp vụ (mốc bàn giao thuộc loại nào), và Gantt sẽ phải đoán
 * hình vẽ từ một nhãn người dùng tự đặt tên.
 */
export const TaskKind = z.enum(['task', 'milestone', 'phase_gate']);
export const CreateTaskInput = z
    .object({
    checklist: ChecklistItemsInput.optional(),
    title: z.string().trim().min(1).max(500),
    description: z.string().max(20_000).optional(),
    /**
     * Bắt buộc với việc thường, **bỏ qua** với mốc — APM-TASK-ms2a. Mốc gửi kèm
     * loại thì lệnh tạo vẫn ghi `null`: mốc không có loại việc.
     */
    taskTypeId: z.uuid().optional(),
    /** Bỏ trống = việc thường. Xem `TaskKind`. */
    kind: TaskKind.optional(),
    /** Bỏ trống = cột mặc định của project. */
    statusId: z.uuid().optional(),
    /** Có giá trị = subtask (tầng 2). Không cho lồng sâu hơn — spec 9.1. */
    parentId: z.uuid().optional(),
    assigneeId: z.uuid().optional(),
    priority: TaskPriority.optional(),
    estimateHours: z.number().positive().max(10_000).optional(),
    storyPoints: z.number().int().min(0).max(1_000).optional(),
    startDate: CalendarDate.optional(),
    dueDate: CalendarDate.optional(),
})
    .superRefine((input, ctx) => {
    if ((input.kind ?? 'task') !== 'milestone' && input.taskTypeId === undefined) {
        ctx.addIssue({ code: 'custom', path: ['taskTypeId'], message: 'Chọn loại việc' });
    }
});
export const TaskView = z.object({
    checklist: TaskChecklist.nullable().optional(),
    checklistVersion: z.number().int().nonnegative().optional(),
    canToggleChecklist: z.boolean().optional(),
    /**
     * Người đang xem có sửa được **đúng việc này** không — APM-TASK-own.
     *
     * Không phải quyền cấp dự án: với CONTRIBUTOR, `task:update` là phạm vi `own`
     * nên câu trả lời khác nhau ở từng dòng. Giao diện đọc cờ này để khỏi mời
     * người ta kéo một thẻ rồi nhận 404 — cửa thật vẫn nằm ở server.
     *
     * Thiếu (`undefined`) thì giao diện coi như **được**: khoá nhầm người có
     * quyền là hỏng nặng hơn, và server vẫn chặn ở đường đi thật.
     */
    canEdit: z.boolean().optional(),
    /**
     * Người đang xem kéo được việc này giữa các cột không — APM-TSP-b, theo
     * `taskStatusRules` của dự án (`canMoveTask`). Không phải `canEdit`: rule mặc
     * định cho thành viên kéo cả việc họ không sửa được.
     *
     * Thiếu thì coi như **được**, cùng lý do với `canEdit`.
     */
    canMove: z.boolean().optional(),
    /** Chuyển được vào / ra khỏi cột hoàn thành không (`canCompleteTask`). */
    canComplete: z.boolean().optional(),
    /**
     * Đổi được ngày bắt đầu / hạn của **đúng việc này** không — APM-TSCH-a, theo
     * `taskScheduleRules` của dự án (`canScheduleTask`). Thiếu thì giao diện dùng quyền cấp dự
     * án (`project:update`) như trước — cửa thật vẫn ở server.
     */
    canSchedule: z.boolean().optional(),
    /**
     * Sớm nhất lúc nào việc này được chuyển sang Xong — APM-TMIN-a, theo
     * `minTaskDurationMinutes` của dự án (`completableAt`). `null`/thiếu = không
     * giới hạn. Giao diện dùng nó để mở hộp thoại **trước** khi gọi API; cửa thật
     * ở server.
     */
    completableAt: z.iso.datetime().nullable().optional(),
    /**
     * Trường còn thiếu để việc được sang "đang làm" / "xong" — APM-EREF-a. Rỗng hoặc
     * thiếu = đủ (hoặc dự án tắt cửa). Giao diện mở popup **trước** khi gọi API; cửa
     * thật ở server.
     */
    planMissing: z.array(PlanField).optional(),
    /**
     * Lần ước lượng theo việc mẫu gần nhất — APM-EREF-d. `referenceKey` chỉ có khi
     * người xem đọc được dự án của việc mẫu (luật #5); `null` = nhập tay vì không có
     * mẫu, hoặc mẫu nằm ở dự án người xem không vào được. `needsReview` = chênh quá
     * 50%, PM nên xem lại.
     */
    estimateReference: z
        .object({
        referenceKey: z.string().nullable(),
        manual: z.boolean(),
        ratioPct: z.number().int(),
        /** `null` = điền SP khi việc chưa giao ai; giờ tự tính lúc giao (APM-EREF-i). */
        estimateHours: z.number().nullable(),
        needsReview: z.boolean(),
        /**
         * Phần "Chi tiết" của khối ước lượng ở drawer (người dùng yêu cầu 2026-11-02):
         * mở việc mẫu và đặt số của nó cạnh số của việc này. Cùng luật với
         * `referenceKey` — `null` khi người xem không đọc được dự án của mẫu, kể cả
         * hai con số **chụp lúc chọn** (`reference*`). Thiếu = bản API cũ.
         */
        referenceTaskId: z.uuid().nullable().optional(),
        referenceProjectId: z.uuid().nullable().optional(),
        referenceTitle: z.string().nullable().optional(),
        referenceActualHours: z.number().nullable().optional(),
        referenceStoryPoints: z.number().int().nullable().optional(),
        /** SP tính ra lúc chọn mẫu — `task.storyPoints` có thể đã bị sửa sau đó. */
        storyPoints: z.number().int().nullable().optional(),
    })
        .nullable()
        .optional(),
    /**
     * Xoá được việc này không — APM-TDEL-a: PM của dự án (`task:delete`) hoặc
     * người tạo việc. Thiếu thì coi như **không** — khác `canEdit`: nút xoá hiện
     * sai là một cú bấm làm mất việc của người khác, dù server vẫn chặn.
     */
    canDelete: z.boolean().optional(),
    /**
     * Kéo được việc này giữa backlog và sprint không — APM-SPR-own: PM của dự án
     * (`project:update`) hoặc người tạo việc. Thiếu thì coi như **không**.
     */
    canPlanSprint: z.boolean().optional(),
    /**
     * Việc ở backlog của dự án Agile — không cập nhật tiến độ được cho tới khi vào
     * sprint (`isAgileBacklog`, cửa thật ở `UpdateProgressHandler`). Thiếu thì coi
     * như **không**: Replay và bản nháp không biết phương pháp dự án.
     */
    inBacklog: z.boolean().optional(),
    id: z.uuid(),
    projectId: z.uuid(),
    /** Mã hiển thị `{project.key}-{number}`. */
    key: z.string(),
    number: z.number().int().positive(),
    title: z.string(),
    description: z.string().nullable(),
    /**
     * `null` chỉ ở **mốc** — APM-TASK-ms2a, người dùng chốt 2026-09-25: mốc là một
     * điểm trên lịch, không tốn công, nên không có loại việc. Việc thường luôn có.
     */
    taskTypeId: z.uuid().nullable(),
    taskTypeName: z.string().nullable(),
    /**
     * Màu do admin đặt cho loại task, hoặc `null`.
     *
     * `null` **không** có nghĩa là không có màu: giao diện tự suy ra một màu ổn
     * định từ `taskTypeId` (xem `TaskTypeTag`). Nếu để trống thành xám hết thì
     * mọi loại trông như nhau, và một danh sách trăm dòng lại phải đọc từng chữ
     * mới biết dòng nào là loại gì.
     */
    taskTypeColor: z.string().nullable(),
    /** Việc định kỳ sinh ra task này — APM-REC-c. Vắng mặt/`null` = task thường. */
    recurrenceId: z.uuid().nullable().optional(),
    statusId: z.uuid(),
    statusName: z.string(),
    statusCategory: WorkflowCategory,
    parentId: z.uuid().nullable(),
    /** `null` = đang ở backlog. */
    sprintId: z.uuid().nullable(),
    /**
     * Nhóm việc chứa task này — APM-1306. `null` = chưa thuộc nhóm nào.
     *
     * Chỉ có id: tên nhóm đi kèm danh sách nhóm của dự án, nên nó không bị nhân
     * bản trên mỗi task và một lần đổi tên không phải sửa hàng trăm bản sao.
     */
    groupId: z.uuid().nullable(),
    assigneeId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    /**
     * Người tạo ra việc này — `task.created_by`.
     *
     * `null` = **không dựng lại được**, không phải "không có ai tạo": Replay và
     * bản nháp AI không mang thông tin này (`TaskState` không có `createdBy`).
     */
    createdById: z.uuid().nullable(),
    /** `null` khi người tạo đã rời tổ chức, hoặc khi `createdById` là `null`. */
    createdByName: z.string().nullable(),
    /**
     * Người tạo có phải PM của dự án **ở thời điểm đọc** không — APM-TASK-creator.
     *
     * Ba giá trị, và `null` khác hẳn `false`:
     * - `false` = việc do người khác tự thêm, **không** nằm trong kế hoạch PM lập.
     *   Giao diện đánh dấu ở board, danh sách và Gantt.
     * - `null` = **chưa biết** (Replay, bản nháp). Không đánh dấu gì.
     *
     * Gộp hai thứ ấy làm một thì tua lại lịch sử sẽ gắn dấu "người khác tự thêm"
     * lên *mọi* việc — một khẳng định bịa ra từ chỗ không có dữ liệu, đúng lỗi mà
     * `assigneeName` của `gantt-adapter` đã tránh.
     *
     * ⚠️ Tính theo đội hình **hiện tại**, không phải ảnh chụp lúc tạo: bàn giao PM
     * thì dấu trên các việc cũ đổi theo. Đánh đổi có chủ đích — `event_log` không
     * mang vai trò người tạo nên một ảnh chụp sẽ trống với toàn bộ việc đã có.
     */
    createdByPm: z.boolean().nullable(),
    priority: TaskPriority,
    estimateHours: z.number().nullable(),
    /**
     * Thời gian **thực tế** — APM-TASK-j, đặt cạnh kế hoạch ở trên.
     *
     * Ba trường đi cùng nhau và phải đọc cùng nhau: cả giá trị của chúng nằm ở
     * chỗ **so với kế hoạch**. Ước 8 giờ mà làm 20 giờ là thông tin; "làm 20
     * giờ" một mình thì không.
     *
     * `actualStartAt` do hệ thống đặt (lần đầu vào cột "đang làm"),
     * `actualEndAt` cũng vậy (vào cột "xong"), còn `actualHours` là tổng giờ bấm
     * đồng hồ — xem `actualHoursSource` và `model Task`. Không sửa tay được
     * (APM-TDRW-d).
     */
    actualStartAt: z.iso.datetime().nullable(),
    actualEndAt: z.iso.datetime().nullable(),
    /** `actualStartAt` do người dùng **khai tay** (APM-TMIN-a), không phải hệ thống đo. */
    actualStartDeclared: z.boolean().optional(),
    actualHours: z.number().nullable(),
    /**
     * `timer` = `actualHours` là tổng giờ bấm, hệ thống tự ghi; `manual` = số có từ
     * trước khi bỏ quyền sửa tay (APM-TDRW-d) trên việc chưa ai bấm giờ, hoặc số của
     * bộ sinh demo. Tuỳ chọn để bản ghi cũ (replay, mẫu) vẫn hợp lệ.
     */
    actualHoursSource: z.enum(['timer', 'manual']).optional(),
    /**
     * Tổng giờ **bấm đồng hồ** trên việc này (các đoạn `work_session` đã đóng) —
     * APM-ACHEAT-e. `null` = chưa ai bấm giờ. Từ APM-ACTH-a nó bằng `actualHours`
     * khi nguồn là `timer`; khác nhau chỉ khi quản lý đã sửa tay.
     */
    trackedHours: z.number().nullable(),
    storyPoints: z.number().int().nullable(),
    /** Của task cha thì **roll-up từ subtask**, không nhập tay (spec 9.1). */
    progressPct: z.number().int().min(0).max(100),
    startDate: CalendarDate.nullable(),
    dueDate: CalendarDate.nullable(),
    /**
     * Kế hoạch gốc lúc chốt baseline — cột `baseline_start_date`/`baseline_due_date`.
     *
     * `null` khi dự án chưa chốt baseline, hoặc việc được tạo **sau** lần chốt.
     * Optional vì nhiều đường dựng `TaskView` (bộ test, Replay) không có dữ liệu
     * này; thiếu thì giao diện nói "chưa chốt", không bịa một mốc.
     */
    baselineStartDate: CalendarDate.nullable().optional(),
    baselineDueDate: CalendarDate.nullable().optional(),
    /**
     * Mốc, cổng pha, hay việc thường — APM-ROLE-r.
     *
     * Là một cột của `task`, nhưng trước đây chỉ `GanttBar` mang nó ra ngoài. Hệ
     * quả: muốn biết mốc của dự án thì phải gọi cả `GET /gantt`, tức chạy CPM
     * toàn dự án để lấy vài dòng. `GanttBar.kind` giờ thừa nhưng vẫn giữ — hai
     * chỗ đọc **cùng một cột trong cùng một câu truy vấn**, nên chúng không lệch
     * được, và bỏ đi thì mọi chỗ vẽ Gantt phải sửa theo.
     */
    kind: TaskKind,
    isBlocked: z.boolean(),
    blockReason: z.string().nullable(),
    position: z.number().int().nonnegative(),
    subtaskCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
});
/**
 * Tải của một người trong một ngày, **cộng dồn qua mọi project**.
 *
 * ⚠️ Chỉ có số, tuyệt đối không có tiêu đề hay mã task. Người gọi có thể không
 * phải thành viên của những project kia — rò tên task qua đường "cảnh báo
 * capacity" là rò dữ liệu chéo, và nó đi vòng qua đúng cái row-level filter mà
 * luật #5 dựng lên.
 */
export const WorkloadDay = z.object({
    date: CalendarDate,
    committedHours: z.number().nonnegative(),
    capacityHours: z.number().nonnegative(),
});
export const AssignmentWarning = z.object({
    code: z.enum([
        'assignee_overloaded',
        'task_exceeds_wbs_limit',
        'due_before_start',
        /** Cột nhận thẻ đã vượt WIP limit. Cảnh báo, không chặn — spec 16.1. */
        'wip_limit_exceeded',
        /** Ước lượng nằm ngoài khoảng 8/80 của PMBOK — APM-1308a. */
        'estimate_below_standard',
        'estimate_above_standard',
        /** Subtask của cùng một hạng mục giao cho quá nhiều người — APM-1308a. */
        'too_many_people',
        /**
         * Bắt đầu làm khi chưa có ước lượng giờ — APM-BOARD-g.
         *
         * **Cảnh báo, không chặn** — cùng lý do với cả nhóm quy chuẩn ở
         * `settings.ts`: một cửa chặn sẽ khiến người dùng nhập số giả để đi qua, và
         * khi đó dữ liệu ước lượng — thứ nuôi velocity ở mục 10 — thành rác. Thiếu
         * ước lượng thì velocity mất một mẫu; ước lượng bịa thì velocity sai.
         */
        'started_without_estimate',
    ]),
    message: z.string(),
    /** Những ngày vượt capacity. Chỉ số, không tên. */
    days: z.array(WorkloadDay).optional(),
});
/**
 * Task vừa tạo/sửa, kèm cảnh báo.
 *
 * **Cảnh báo, không chặn** — HTTP vẫn 201. Chặn cứng làm PM không giao được
 * việc trong những lúc buộc phải over-allocate, và họ sẽ tắt tính năng.
 */
export const TaskMutationResult = z.object({
    task: TaskView,
    warnings: z.array(AssignmentWarning),
});
export const UpdateTaskInput = z.object({
    checklist: ConfigureChecklistInput.optional(),
    title: z.string().trim().min(1).max(500).optional(),
    /**
     * Chuyển task vào một **nhóm** (task cha), hoặc `null` để đưa ra ngoài —
     * APM-1306.
     *
     * "Nhóm" và "task cha" là cùng một thứ: spec 9.1 cho hai tầng, và tầng 1 là
     * chỗ tự nhiên để gom việc theo pha hay theo hạng mục. Không thêm một thực
     * thể `phase` riêng — nó sẽ cần bảng, endpoint, event, và một câu trả lời cho
     * "phase có xuất hiện trên burndown không" mà tầng cha đã trả lời sẵn.
     *
     * ⚠️ Chỉ nhận **hai** tầng: cha phải là task chưa có cha, và task đang
     * chuyển phải chưa có con. Xem `applyParent`.
     */
    parentId: z.uuid().nullable().optional(),
    description: z.string().max(20_000).nullable().optional(),
    taskTypeId: z.uuid().optional(),
    /**
     * Đổi giữa việc thường và mốc — APM-TASK-f.
     *
     * ⚠️ Task có việc con **không** thành mốc được: mốc là một thời điểm, còn một
     * thời điểm thì không chứa công việc bên trong. Cửa chặn ở tầng lệnh.
     */
    kind: TaskKind.optional(),
    priority: TaskPriority.optional(),
    estimateHours: z.number().positive().max(10_000).nullable().optional(),
    // `actualHours` không còn sửa được — chỉ tính từ bấm giờ (APM-TDRW-d, người dùng
    // yêu cầu 2026-11-02). Client cũ gửi kèm thì Zod bỏ qua.
    storyPoints: z.number().int().min(0).max(1_000).nullable().optional(),
    startDate: CalendarDate.nullable().optional(),
    dueDate: CalendarDate.nullable().optional(),
});
export const ChangeStatusInput = z.object({
    statusId: z.uuid(),
    /** Vị trí mới trong cột. Bỏ trống = cuối cột. */
    position: z.number().int().min(0).optional(),
    /**
     * Lý do đánh dấu xong khi **chưa tick hết** Definition of Done — APM-AGILE-d.
     *
     * ## Vì sao có đường này, thay vì chặn cứng
     *
     * Chặn cứng là thứ làm đội tắt tính năng trong tuần đầu: luôn có việc mà một
     * mục DoD không áp dụng ("viết tài liệu API" cho một task sửa CSS). Bắt viết
     * một câu là đủ để người ta dừng lại một nhịp, và câu ấy đi vào event — nên
     * "vì sao việc này xong mà chưa review" có chỗ tra, thay vì là một khoảng
     * trống ai cũng đoán.
     */
    dodSkipReason: z.string().trim().min(1).max(500).optional(),
    /**
     * Khai lại giờ **bắt đầu thực tế** trong cùng lần chuyển — APM-TMIN-a.
     *
     * Người quên chuyển sang "đang làm" lúc bắt đầu thì tới lúc xong mới khai.
     * Cùng một lệnh với lần chuyển (không phải một lời gọi riêng trước đó): cửa
     * thời gian tối thiểu đọc mốc mới, và hai thứ cùng thành hoặc cùng hỏng.
     * Không ở tương lai, lùi tối đa `MAX_DECLARED_START_DAYS` ngày.
     */
    actualStartAt: z.iso.datetime({ offset: true }).optional(),
    /**
     * Nút "Bắt đầu làm" nhanh (chi tiết việc) — người dùng chốt 2026-10-30: bắt đầu là
     * làm trong **sprint đang chạy**. Việc ở backlog vốn đã theo luật ấy; cờ này áp nó
     * cho cả việc nằm trong sprint **chưa chạy** (planning): việc sang sprint đang chạy,
     * không có sprint nào đang chạy ⇒ 409 `TASK_START_NO_ACTIVE_SPRINT`. Kéo thẻ trên
     * board của một sprint chưa chạy không gửi cờ, nên vẫn chạy như cũ.
     */
    intoRunningSprint: z.boolean().optional(),
});
// ── Definition of Done — APM-AGILE-d ────────────────────────────────────────
export const DodItemView = z.object({
    id: z.uuid(),
    text: z.string(),
    position: z.number().int().nonnegative(),
    /** `false` = đã bỏ dùng; vẫn trả về để task cũ tra ngược được chữ đã tick. */
    isActive: z.boolean(),
});
export const CreateDodItemInput = z.object({
    text: z.string().trim().min(1).max(200),
});
/** Checklist của **một** task: mục nào đang bật, và mục nào đã tick. */
export const TaskDodView = z.object({
    taskId: z.uuid(),
    items: z.array(DodItemView.extend({
        checked: z.boolean(),
        /** `null` khi chưa tick. */
        checkedBy: z.uuid().nullable(),
    })),
});
export const AssignTaskInput = z.object({
    /** `null` = gỡ người phụ trách. */
    assigneeId: z.uuid().nullable(),
});
export const BlockTaskInput = z.object({
    blocked: z.boolean(),
    /**
     * Chọn từ danh sách lý do thường gặp, không phải ô text tự do
     * (docs/frontend-guide.md §3) — để thống kê được nguyên nhân, và để
     * `APM-1001` phân biệt "chậm do năng lực" với "chậm do ngoại cảnh".
     */
    reason: z.string().max(200).optional(),
});
export const ProgressInput = z.object({
    progressPct: z.number().int().min(0).max(100),
    /** Khai lại giờ bắt đầu thực tế khi kéo lên 100% — APM-TMIN-a, như `ChangeStatusInput`. */
    actualStartAt: z.iso.datetime({ offset: true }).optional(),
});
/**
 * Kéo một hàng lên/xuống trong danh sách Gantt — APM-GANTT-h.
 *
 * Nhận **`beforeTaskId`** chứ không nhận chỉ số. Danh sách trên màn hình có thể
 * đang lọc (chỉ đường găng, chỉ một sprint) hoặc gấp bớt nhóm, nên chỉ số hàng
 * mà client thấy không phải chỉ số trong dự án — gửi số đi là gửi một con số chỉ
 * đúng với đúng bộ lọc đang bật.
 *
 * `null` = thả xuống cuối nhóm/danh sách.
 */
export const ReorderGanttInput = z.object({
    taskId: z.uuid(),
    beforeTaskId: z.uuid().nullable(),
});
export const DependencyInput = z.object({
    fromTaskId: z.uuid(),
    toTaskId: z.uuid(),
    type: DependencyType.default('finish_to_start'),
    lagDays: z.number().int().min(-365).max(365).default(0),
});
export const DependencyView = z.object({
    id: z.uuid(),
    fromTaskId: z.uuid(),
    toTaskId: z.uuid(),
    type: DependencyType,
    lagDays: z.number().int(),
});
/**
 * Thứ form tạo dự án cần biết **trước khi** vẽ — APM-ROLE-m2b.
 *
 * Câu hỏi "tôi có phải chọn quản lý trực tiếp không" chỉ server trả lời được:
 * nó phụ thuộc vai trò tổ chức, `line_manager_id` hiện có, và việc người này có
 * đang quản lý ai không. Để client tự đoán là chép `decideApproval` sang một
 * bản thứ hai, và bản không ai test sẽ trôi.
 */
export const ProjectCreationContext = z.object({
    /** `true` = form phải bắt chọn quản lý trực tiếp, nếu không server trả 400. */
    needsLineManager: z.boolean(),
    /**
     * Tên người sẽ duyệt, nếu đã xác định được. `null` khi mở thẳng **hoặc** khi
     * chưa chọn quản lý — hai trạng thái khác nhau, phân biệt bằng
     * `needsLineManager`.
     */
    approverName: z.string().nullable(),
    /**
     * Người có thể chọn làm quản lý. Rỗng khi `needsLineManager` là `false`.
     *
     * ⚠️ Đây là danh sách người trong **tổ chức của chính mình**, và nó chỉ trả
     * về tên với phòng ban — không email, không vai trò, không id tổ chức. Để
     * chọn được quản lý thì phải thấy người, nhưng không cần thấy gì hơn thế.
     */
    candidates: z.array(z.object({
        userId: z.uuid(),
        displayName: z.string(),
        departmentName: z.string().nullable(),
    })),
});
/** Tick hay bỏ tick một mục DoD — APM-AGILE-d. */
export const ToggleDodInput = z.object({
    checked: z.boolean(),
});
//# sourceMappingURL=project.js.map